// v2 palm — a curved, leaning coconut palm: the trunk is a chain of tapering
// segments bowing along a quadratic curve with darker ring collars at the joints
// (the ringed bark), crowned by long fronds that genuinely arch — each rising
// from the hub then breaking over and drooping through three chained blade
// segments — above a hanging cluster of coconuts and two dead brown fronds
// slumped against the trunk. Each variant bows in its own direction and strength
// and re-deals the frond fan. Ported 1:1 from age-of-mcp's v2/tree-palm.ts, using
// the library's shared `tropic` (blade, h01, lerp, tube) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part, type Vec3 } from "../../lib";

const BARK = "#a8895c";
const RING = "#83683f";
const HUB = "#6b5128";
const LEAF = "#3f9c4c";
const LEAF_DEEP = "#2f7f3e";
const LEAF_DEAD = "#a98c4f";
const NUT = "#5f4a2c";
const NUT_2 = "#74583a";

function palmParts(v: number): Part[] {
  const seed = v * 13 + 3;
  const leanA = v * 2.3 + tropic.h01(seed, 0) * 0.8;     // bow direction
  const lean = tropic.lerp(0.24, 0.38, tropic.h01(seed, 1));    // bow strength (tiles)
  const H = 1.82;                                  // trunk-top height
  const SEG = 4;
  // Trunk joints along a quadratic bow — straight at the root, leaning
  // harder toward the crown.
  const joint = (t: number): Vec3 => [
    Math.cos(leanA) * lean * t * t, H * t, Math.sin(leanA) * lean * t * t,
  ];
  const parts: Part[] = [];
  for (let i = 0; i < SEG; i++) {
    const r0 = tropic.lerp(0.13, 0.07, i / SEG);
    const r1 = tropic.lerp(0.13, 0.07, (i + 1) / SEG);
    parts.push(tropic.tube(joint(i / SEG), joint((i + 1) / SEG), r0, r1, BARK, 7));
    if (i > 0) {
      // ring collar wrapping the joint — the ringed bark
      parts.push(tropic.tube(joint(i / SEG - 0.012), joint(i / SEG + 0.022),
        r0 * 1.22, r0 * 1.18, RING, 7));
    }
  }
  const top = joint(1);
  parts.push({ geometry: new THREE.IcosahedronGeometry(0.12, 0), color: HUB,
    position: [top[0], top[1], top[2]] });

  // Seven fronds arching out of the hub.
  const N = 7;
  for (let i = 0; i < N; i++) {
    const a = leanA + (i / N) * Math.PI * 2 + tropic.h01(seed, 10 + i) * 0.5;
    parts.push(...tropic.blade([top[0], top[1] + 0.05, top[2]], a, {
      segs: 3, len: tropic.lerp(0.9, 1.05, tropic.h01(seed, 20 + i)), width: 0.2,
      thick: 0.035, rise: 0.5 - tropic.h01(seed, 30 + i) * 0.22, droop: 0.55,
      taper: 0.45,
      colors: i % 2 ? [LEAF, LEAF_DEEP, LEAF] : [LEAF_DEEP, LEAF, LEAF_DEEP],
    }));
  }
  // Two dead fronds slumped steeply down against the trunk.
  for (let i = 0; i < 2; i++) {
    const a = leanA + Math.PI * (0.55 + i * 0.9) + tropic.h01(seed, 40 + i) * 0.4;
    parts.push(...tropic.blade([top[0], top[1] - 0.02, top[2]], a, {
      segs: 1, len: 0.5, width: 0.12, thick: 0.03,
      rise: -1.05, droop: 0, colors: [LEAF_DEAD],
    }));
  }
  // Hanging coconut cluster under the hub.
  const nuts: ReadonlyArray<readonly [number, number, string]> = [
    [0.1, 0.04, NUT], [-0.07, 0.09, NUT_2], [0.0, -0.09, NUT],
  ];
  for (const [nx, nz, c] of nuts) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.07, 0), color: c,
      position: [top[0] + nx, top[1] - 0.14, top[2] + nz] });
  }
  return parts;
}

export const treePalmV2 = defineModel({
  id: "tree-palm-v2",
  batchId: "fable",
  name: "Tree palm (v2)",
  tags: ["scenery","tree","palm","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree palm.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.5,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => palmParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => palmParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => palmParts(2) },
  ],
  animations: [],
});
